import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { motion } from "motion/react";

import {
  type EventCondition,
  isInlineTraitColumn,
  type JourneySetCondition,
  type NumberOfCondition,
  type PropertyCondition,
  type ReferencedPropertyCondition,
  type RootCondition,
  type SegmentSetCondition,
} from "@hightouch/lib/query/visual/types";
import { Button, Column, Row, AgentsIcon, useToast } from "@hightouch/htui";

import type { MinimalParentModel } from "src/components/audiences/types";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import {
  createChildConditionUpdater,
  groupSubcondition,
  type OnUpdate,
  ungroupSubcondition,
} from "src/components/explore/utils/condition-builders";
import type { RelationshipWithoutFilterableColumnsFragment } from "src/graphql";
import {
  type AndCondition,
  type AndOrCondition,
  type Audience,
  type Condition,
  ConditionType,
  type FilterableColumn,
  type FunnelCondition,
  type OrCondition,
  type PropertyComparisonType,
  isRelatedColumn,
  isTraitCondition,
} from "src/types/visual";

import { AddFilterButton, AndOrToggleButton } from "./condition-buttons";
import {
  ConditionWrapper,
  NestedAndOrConditionWrapper,
} from "./condition-wrapper";
import { EventFilter } from "./event-filter";
import { JourneySetFilter } from "./journey-set-filter";
import { NumberOfFilter } from "./number-of-filter";
import { PropertyFilter } from "./property-filter";
import { ReferencePropertyFilter } from "./reference-property-filter";
import { SegmentSetFilter } from "./segment-set-filter";
import { getTraitDefinitionIdFromColumn } from "./utils";
import {
  AudienceWaterfall,
  useShouldShowWaterfall,
} from "src/components/explore/audience-waterfall";
import type { Updater, UpdaterAdvanced } from "src/utils/immutable-helpers";
import {
  DragList,
  useAnimatingElement,
  useDragGhostHeight,
  useDragList,
  useDropTarget,
  useMotionProps,
} from "src/components/explore/draggable";
import { dragPreviewId, isCompactCondition } from "./constants";
import { captureException } from "src/lib/monitoring";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";

interface GhostCondition {
  type: "ghost";
  id: "ghost";
}

export interface CommonProps {
  audience: Audience | undefined;
  parent: MinimalParentModel | undefined | null;
  onRemove?: () => void;
  onUngroup?: () => void;
  isEventTrait?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  // Optional model id that will be used to fetch filterable columns
  modelId?: string | null;
  onGroup?: () => void;
}

interface ConditionFieldProps extends CommonProps {
  events?: RelationshipWithoutFilterableColumnsFragment[];
  condition: AndOrCondition<Condition>;
  level?: number;
  isLoading?: boolean;
  propertyComparisonType?: PropertyComparisonType;
  lhsPropertyFilterVariant?: "dropdown" | "text"; // For property filter
  /**
   * Most filters use the columns off of the parent. However they may be overridden if the columns prop is provided
   * This is used for the funnel filter to display the columns for the funnel condition's (parent) event model
   */
  columns?: FilterableColumn[];
  onUpdate: (
    updater: Updater<Condition | AndCondition | OrCondition>,
    options?: { checkpoint?: boolean },
  ) => void;
  /** Only used for level 0 group conditions */
  conditionGroupWaterfall?: React.ReactNode;
  disableDrag?: boolean;
}

interface AndOrConditionFieldProps
  extends Omit<ConditionFieldProps, "onUpdate"> {
  condition:
    | AndCondition<AndOrCondition<Condition>>
    | OrCondition<AndOrCondition<Condition>>;
  onUpdate: (
    updater: Updater<AndCondition | OrCondition>,
    options?: { checkpoint?: boolean },
  ) => void;
  level?: number;
  /** Render prop for an inline agent chat, rendered in place of the Add filter button at level 0. */
  renderInlineAgent?: (props: {
    isOpen: boolean;
    onClose: () => void;
  }) => React.ReactNode;
  /** Render prop for root-level suggestion chips, rendered alongside the Add filter button at level 0. */
  renderRootLevelSuggestions?: () => React.ReactNode;
}

export interface FilterProps<TCondition extends Condition | FunnelCondition>
  extends CommonProps {
  columns?: FilterableColumn[];
  propertyComparisonType?: PropertyComparisonType;
  includeParentInPropertyComparison?: boolean;
  condition: TCondition;
  onUpdate: (
    updater: UpdaterAdvanced<Condition | FunnelCondition, TCondition>,
  ) => void;
  level?: number;
}

export const AndOrConditionField: FC<Readonly<AndOrConditionFieldProps>> = (
  props,
) => {
  const { onUpdate, condition, level } = props;
  // Preserves uuid
  const addAtIndex = useCallback(
    (index: number, item: Condition) => {
      onUpdate(
        (draft) => {
          draft.conditions.splice(index, 0, item);
        },
        // We want the addition to be merged into the removal for undo purposes
        { checkpoint: false },
      );
    },
    [onUpdate],
  );

  const remove = useCallback(
    (id: string) => {
      // Because move happens in two operations, we need to wait until all operations complete before checking
      // if the parent should be deleted (due to having no children).
      onUpdate((draft) => {
        const index = draft.conditions.findIndex((c) => c.id === id);
        if (index >= 0) {
          draft.conditions.splice(index, 1);
        }
      });
    },
    [onUpdate],
  );

  return (
    <DragList
      id={condition.id}
      level={level ?? -1}
      count={condition.conditions.length}
      addAtIndex={addAtIndex}
      remove={remove}
      data={condition.type}
    >
      <AndOrConditionFieldInternal {...props} />
    </DragList>
  );
};

type DivAnimationProps = React.ComponentProps<typeof motion.div>;

interface GhostConditionProps {
  conditionType: ConditionType.And | ConditionType.Or;
  animationProps: DivAnimationProps;
  includeWaterfall?: boolean;
}

const noop = () => {};

const GhostCondition = (props: GhostConditionProps) => {
  const { includeWaterfall } = props;
  const height = useDragGhostHeight({ includeWaterfall });
  return (
    <motion.div
      // We don't want framer motion to animate the full start -> end on drop since it's coming from the cursor instead
      {...props.animationProps}
      layoutId="ghost"
    >
      <Column gap={4} mb={4} width="100%">
        <Column
          minWidth={0}
          height={`${height}px`}
          borderRadius="xl"
          border="1px dashed" // 0.5px is too thin to see
          borderColor="base.border"
          backgroundColor="base.background"
        />
        <AndOrToggleButton
          isDisabled
          conditionType={props.conditionType}
          onClick={noop}
        />
      </Column>
    </motion.div>
  );
};

const useAnimationProps = (args: {
  id?: string;
  animationId: string;
  /**
   * Opt out of shared-layout animation entirely. The drag store enables
   * animation from its initial state, so every condition otherwise mounts with
   * a `layoutId` and `layout="position"` inside the builder's `LayoutGroup`
   * whether or not a drag is in progress. Compact has no drag, so that
   * machinery buys nothing and costs a layout projection per row.
   */
  isDisabled?: boolean;
}) => {
  const { id, animationId, isDisabled = false } = args;
  const enableAnimation = useDragList((state) => state.enableAnimation);
  const onLayoutAnimationComplete = useDragList(
    (state) => state.onLayoutAnimationComplete,
  );
  const motionProps = useMotionProps();
  const value = useMemo(() => {
    return enableAnimation && !isDisabled
      ? {
          layoutId: animationId,
          layout: "position" as const,
          transition:
            "transition" in motionProps ? motionProps.transition : undefined,
          style: { willChange: "transform" },
          initial: false,
          onLayoutAnimationComplete: () =>
            onLayoutAnimationComplete({ id, animationId }),
        }
      : { transition: { type: false as const, initial: false } };
  }, [
    animationId,
    enableAnimation,
    isDisabled,
    id,
    motionProps,
    onLayoutAnimationComplete,
  ]);
  return value;
};

const AndOrConditionFieldInternal: FC<Readonly<AndOrConditionFieldProps>> = ({
  condition,
  level = 0,
  onUpdate,
  onRemove,
  onUngroup,
  conditionGroupWaterfall,
  disableDrag,
  renderInlineAgent,
  renderRootLevelSuggestions,
  ...props
}) => {
  const { variant } = useQueryBuilderContext();
  // The compact rail doubles as the AND/OR toggle, so it can't also be a grab
  // handle. Disabling drag for the whole builder — nested groups included —
  // also means no ghost, no drag preview and no layout animations.
  const dragDisabled = disableDrag || variant === "compact";
  const isCompactRoot = isCompactCondition(variant, level);

  const draggingIndex = useDragList((state) => state.draggingIndex);
  const enableAnimation = useDragList((state) => state.enableAnimation);
  const onLayoutAnimationComplete = useDragList(
    (state) => state.onLayoutAnimationComplete,
  );
  const ghostIndex = useDragList((state) => state.ghostIndex);
  const animatingElement = useAnimatingElement();
  const { ref: addFilterRowRef } = useDropTarget({
    index: condition.conditions.length,
  });

  const [isInlineAgentOpen, setIsInlineAgentOpen] = useState(false);

  const add = useCallback(
    (newCondition: Condition) => {
      onUpdate((draft) => {
        draft.conditions.push({ ...newCondition, id: crypto.randomUUID() });
      });
    },
    [onUpdate],
  );

  const toggleAndOr = useCallback(
    () =>
      onUpdate((draft) => {
        draft.type =
          draft.type === ConditionType.And
            ? ConditionType.Or
            : ConditionType.And;
      }),
    [onUpdate],
  );

  const setImmer = useMemo(
    () => createChildConditionUpdater(onUpdate),
    [onUpdate],
  );

  const remove = useCallback(
    (id?: string) => () => {
      if (condition.conditions.length === 1) {
        onRemove?.();
      } else {
        if (!id) {
          captureException(
            new Error("Condition in query builder is missing id"),
          );
        } else {
          onUpdate((draft) => {
            const index = draft.conditions.findIndex((c) => c.id === id);
            if (index >= 0) {
              draft.conditions.splice(index, 1);
            }
          });
        }
      }
    },
    [onUpdate, onRemove, condition.conditions],
  );

  const createInsertGroup = useCallback(
    (index: number) => () => {
      onUpdate((draft) => groupSubcondition(index, draft));
    },
    [onUpdate],
  );

  const createRemoveGroup = useCallback(
    (index: number) => () => {
      onUpdate((draft) => ungroupSubcondition(index, draft));
    },
    [onUpdate],
  );

  const showWaterfall = useShouldShowWaterfall({
    level,
    audienceId: props.audience?.id,
    parentId: props.parent?.id,
    conditionType: condition.type,
  });

  useEffect(() => {
    if (condition.conditions.length === 0 && level !== 0) {
      onRemove?.();
    }
  }, [condition.conditions, onRemove, level]);

  const conditionsWithGhost = useMemo(() => {
    const conditions = (
      condition.conditions as (AndOrCondition<Condition> | GhostCondition)[]
    ).map((c, i) => ({
      ...c,
      __index: i,
    }));
    if (ghostIndex >= 0) {
      conditions.splice(ghostIndex, 0, {
        type: "ghost",
        __index: -1,
        id: "ghost",
      });
    }
    return conditions.filter((c) => {
      if (c.type === "ghost") {
        return true;
      }
      // Do not render the condition being dragged. It's replaced by the ghost + drag preview
      if (draggingIndex >= 0 && c.__index === draggingIndex) {
        return false;
      }
      return true;
    });
  }, [condition.conditions, ghostIndex, draggingIndex]);

  const motionProps = useMotionProps();

  return (
    <>
      {conditionsWithGhost.map((value) => {
        const { __index: index, ...nestedCondition } = value;
        const isTheElementBeingDragged = index === draggingIndex;
        const id = nestedCondition.id;
        // We use a custom id for the drag preview because we only want to animate
        // the drop animation, not the pick up animation, due to pick up
        // animation problems.
        const animationId =
          id && animatingElement === id ? dragPreviewId : (id ?? String(index));
        // TODO: can't memoize inside callback, need to reformat this
        const animationProps = enableAnimation
          ? {
              layoutId: animationId,
              layout: "position" as const,
              transition:
                "transition" in motionProps
                  ? motionProps.transition
                  : undefined,
              style: { willChange: "transform" },
              initial: false,
              onLayoutAnimationComplete: () =>
                onLayoutAnimationComplete({ id, animationId }),
            }
          : { transition: { type: false as const, initial: false } };
        if (nestedCondition.type === "ghost") {
          return (
            <GhostCondition
              key="ghost"
              includeWaterfall={showWaterfall}
              conditionType={condition.type}
              animationProps={animationProps}
            />
          );
        }

        const nestedConditionIsAndOr = [
          ConditionType.And,
          ConditionType.Or,
        ].includes(nestedCondition.type);

        const sharedProps = {
          condition: nestedCondition,
          level: level + 1,
          onUpdate: setImmer(nestedCondition.id),
          onRemove: remove(nestedCondition.id),
        };

        const waterfallProps = showWaterfall
          ? {
              audienceId: props.audience?.id,
              modelId: props.modelId,
              parentModelId: props.parent?.id,
              index,
              condition: condition as AndCondition<RootCondition>,
            }
          : undefined;

        if (nestedConditionIsAndOr) {
          return (
            <motion.div
              key={animationId}
              {...animationProps}
              hidden={isTheElementBeingDragged}
            >
              <NestedAndOrConditionWrapper
                onToggleAndOr={toggleAndOr}
                conditionType={condition.type}
                condition={nestedCondition}
                index={index}
                isDisabled={props.isDisabled}
                disableDrag={dragDisabled}
                includeWaterfall={!!waterfallProps}
              >
                <ConditionField
                  {...props}
                  {...sharedProps}
                  onUngroup={createRemoveGroup(index)}
                  conditionGroupWaterfall={
                    waterfallProps && (
                      <Row px={0} py={2} mt={4}>
                        <AudienceWaterfall {...waterfallProps} />
                      </Row>
                    )
                  }
                />
              </NestedAndOrConditionWrapper>
            </motion.div>
          );
        }

        return (
          <NestedCondition
            key={animationId}
            index={index}
            level={level}
            isRealtimeAudience={props.audience?.is_realtime_audience}
            animationId={animationId}
            nestedCondition={nestedCondition}
            parentCondition={condition}
            isTheElementBeingDragged={isTheElementBeingDragged}
            generateOnUpdate={setImmer}
            generateRemove={remove}
            disableDrag={dragDisabled}
            toggleAndOr={toggleAndOr}
            audience={props.audience}
            add={add}
            createInsertGroup={createInsertGroup}
            onUngroup={onUngroup}
            parent={props.parent}
            showWaterfall={showWaterfall}
            isDisabled={props.isDisabled}
          />
        );
      })}

      {isInlineAgentOpen &&
        renderInlineAgent &&
        renderInlineAgent({
          isOpen: isInlineAgentOpen,
          onClose: () => setIsInlineAgentOpen(false),
        })}
      {level === 0 && renderRootLevelSuggestions?.()}
      {!props.isDisabled && (
        <Row ref={addFilterRowRef} gap={2} alignItems="center" flexWrap="wrap">
          <AddFilterButton
            {...props}
            conditionType={condition.type}
            isCompact={isCompactRoot}
            // With nothing above it, there is no operator to join — naming the
            // button after one would be meaningless.
            hasPrecedingCondition={condition.conditions.length > 0}
            onChange={add}
          />
          {renderInlineAgent && !isInlineAgentOpen && (
            <Button
              variant="secondary"
              icon={AgentsIcon}
              onClick={() => setIsInlineAgentOpen(true)}
            >
              Update with AI
            </Button>
          )}
        </Row>
      )}
      {conditionGroupWaterfall}
    </>
  );
};

interface NestedConditionProps {
  audience: Audience | undefined;
  parent: MinimalParentModel | null | undefined;
  animationId: string;
  isTheElementBeingDragged: boolean;
  index: number;
  nestedCondition: Condition;
  parentCondition: AndCondition<Condition> | OrCondition<Condition>;
  isRealtimeAudience?: boolean;
  isDisabled?: boolean;
  level: number;
  generateOnUpdate: any;
  generateRemove: any;
  toggleAndOr: () => void;
  disableDrag?: boolean;
  isAudienceCreated?: boolean;
  add: (newCondition: Condition) => void;
  createInsertGroup: (index: number) => () => void;
  onUngroup?: () => void;
  showWaterfall?: boolean;
}

/** Conditions nested under a root-connected AndOrCondition */
const NestedCondition = React.memo(function NestedConditionUnmemoized(
  props: NestedConditionProps,
) {
  const {
    audience,
    parent,
    animationId,
    isTheElementBeingDragged,
    index,
    nestedCondition,
    parentCondition,
    isRealtimeAudience,
    isDisabled,
    level,
    generateOnUpdate,
    generateRemove,
    toggleAndOr,
    disableDrag,
    add,
    createInsertGroup,
    onUngroup,
    showWaterfall,
  } = props;

  const { selectCondition, maxNestingDepth, variant } =
    useQueryBuilderContext();
  const isCompact = isCompactCondition(variant, level);
  const { traits } = useFilterableOptionsContext();
  const { toast } = useToast();
  const animationProps = useAnimationProps({
    id: nestedCondition.id,
    animationId,
    isDisabled: isCompact,
  });

  const onUpdate = useMemo(
    () => generateOnUpdate(nestedCondition.id),
    [generateOnUpdate, nestedCondition.id],
  );

  const onRemove = useMemo(
    () => generateRemove(nestedCondition.id),
    [generateRemove, nestedCondition.id],
  );
  const onRemoveForChildren = useMemo(
    () =>
      // TODO: this should probably be moved into the ConditionField switch case.
      nestedCondition.type === ConditionType.Property ? undefined : onRemove,
    [onRemove, nestedCondition.type],
  );

  const isTraitTemplate =
    isTraitCondition(nestedCondition) &&
    traits.find(
      (trait) =>
        trait.id === getTraitDefinitionIdFromColumn(nestedCondition.property),
    )?.is_template;

  const isInlineTrait =
    nestedCondition.type === ConditionType.Property &&
    isRelatedColumn(nestedCondition.property) &&
    isInlineTraitColumn(nestedCondition?.property.column);

  const isEventCondition = nestedCondition.type === ConditionType.Event;

  const isAudienceCreated = audience?.id != null;
  const isRelatedModelCondition =
    nestedCondition.type === ConditionType.NumberOf &&
    nestedCondition.relationshipId != null;
  const canAddAudienceTrait =
    isAudienceCreated &&
    (isTraitTemplate || isRelatedModelCondition || isEventCondition);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const onAddAudienceTrait = useMemo(
    () =>
      canAddAudienceTrait
        ? () =>
            selectCondition({
              condition: nestedCondition,
              update: onUpdate,
            })
        : undefined,
    [
      onUpdate,
      nestedCondition,
      selectCondition,
      canAddAudienceTrait,
      isTraitTemplate,
    ],
  );
  const onAddTrait = isInlineTrait
    ? () =>
        selectCondition({
          condition: nestedCondition,
          update: onUpdate,
        })
    : undefined;

  const duplicate = () => {
    add({ ...nestedCondition });

    toast({
      id: "duplicate-condition-toast",
      title: "Condition duplicated",
      variant: "success",
    });
  };

  return (
    <motion.div {...animationProps} hidden={isTheElementBeingDragged}>
      <ConditionWrapper
        index={index}
        data-testid={`condition-${nestedCondition.id}`}
        isDisabled={isDisabled}
        isRealtimeAudience={isRealtimeAudience}
        disableDrag={disableDrag}
        // `level` here is the enclosing group's level, so this is only true for
        // conditions directly under the root group. Conditions inside a nested
        // group stay at default — that's where "Ungroup" lives.
        isCompact={isCompact}
        conditionType={parentCondition.type}
        condition={nestedCondition}
        disableGrouping={level >= maxNestingDepth}
        onToggleAndOr={toggleAndOr}
        onAddTrait={onAddTrait}
        onAddAudienceTrait={onAddAudienceTrait}
        onDuplicate={duplicate}
        onGroup={createInsertGroup(index)}
        onUngroup={onUngroup}
        onRemove={onRemove}
        footer={
          showWaterfall && (
            <Row
              borderTopWidth="hairline"
              borderTopStyle="solid"
              borderColor="base.border"
              px={4}
              py={2}
            >
              <AudienceWaterfall
                audienceId={audience?.id}
                parentModelId={parent?.id}
                index={index}
                condition={parentCondition as AndCondition<RootCondition>}
              />
            </Row>
          )
        }
      >
        <ConditionField
          {...props}
          onUpdate={onUpdate}
          onRemove={onRemoveForChildren}
          condition={nestedCondition}
          level={level + 1}
        />
      </ConditionWrapper>
    </motion.div>
  );
});

export const ConditionField: FC<Readonly<ConditionFieldProps>> = ({
  condition,
  level = 0,
  onUpdate,
  ...props
}) => {
  switch (condition.type) {
    case ConditionType.And:
    case ConditionType.Or:
      return (
        <Row ml={4} width="100%">
          <Column width="100%">
            <AndOrConditionField
              {...props}
              condition={condition}
              level={level}
              onUpdate={
                // We don't tie `condition` and `onUpdate` together with types
                // so `onUpdate` doesn't get automatically narrowed with `condition`
                onUpdate as OnUpdate<AndCondition | OrCondition>
              }
            />
          </Column>
        </Row>
      );
    case ConditionType.SegmentSet:
      return (
        <SegmentSetFilter
          {...props}
          condition={condition}
          onUpdate={onUpdate as OnUpdate<SegmentSetCondition>}
          level={level}
        />
      );
    case ConditionType.Property:
      return (
        <PropertyFilter
          {...props}
          condition={condition}
          onUpdate={onUpdate as OnUpdate<PropertyCondition>}
          level={level}
        />
      );
    case ConditionType.ReferenceProperty:
      return (
        <ReferencePropertyFilter
          {...props}
          condition={condition}
          onUpdate={onUpdate as OnUpdate<ReferencedPropertyCondition>}
          level={level}
        />
      );
    case ConditionType.NumberOf:
      return (
        <NumberOfFilter
          {...props}
          condition={condition}
          onUpdate={onUpdate as OnUpdate<NumberOfCondition>}
          level={level}
        />
      );
    case ConditionType.Event:
      return (
        <EventFilter
          {...props}
          condition={condition}
          onUpdate={onUpdate as OnUpdate<EventCondition>}
          level={level}
        />
      );
    case ConditionType.JourneySet:
      return (
        <JourneySetFilter
          {...props}
          condition={condition}
          onUpdate={onUpdate as OnUpdate<JourneySetCondition>}
          level={level}
        />
      );
    default:
      return null;
  }
};
